import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { fetchShopProducts, fetchCategories } from '../db/firebaseProducts';
import { Search, ChevronDown, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';

// Constants
const CACHE_KEYS = {
  PRODUCTS: 'cachedShopProducts',
  CATEGORIES: 'cachedCategories',
  TIMESTAMP: 'shopCacheTimestamp'
};

const CACHE_DURATION = 5 * 60 * 1000;
const PRODUCTS_PER_PAGE = 16;

const Shop = () => {
  const { categoryId, subcategoryId } = useParams();
  const navigate = useNavigate();

  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState({});
  const [organizedCategories, setOrganizedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState(categoryId || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(subcategoryId || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleProducts, setVisibleProducts] = useState(new Set());
  const [sortBy, setSortBy] = useState('order'); // NEW: Sort state
  const observerRef = useRef(null);

  // Cache utils
  const isCacheValid = useCallback(() => {
    const timestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
    if (!timestamp) return false;
    return Date.now() - parseInt(timestamp) < CACHE_DURATION;
  }, []);

  const loadFromCache = useCallback(() => {
    try {
      const cachedProducts = localStorage.getItem(CACHE_KEYS.PRODUCTS);
      const cachedCategories = localStorage.getItem(CACHE_KEYS.CATEGORIES);
      if (cachedProducts && cachedCategories) {
        setAllProducts(JSON.parse(cachedProducts));
        const catData = JSON.parse(cachedCategories);
        setCategories(catData);
        const organized = organizeCategoriesHierarchy(Object.values(catData));
        setOrganizedCategories(organized);
        return true;
      }
    } catch (e) {
      localStorage.removeItem(CACHE_KEYS.PRODUCTS);
      localStorage.removeItem(CACHE_KEYS.CATEGORIES);
    }
    return false;
  }, []);

  const saveToCache = useCallback((products, categories) => {
    localStorage.setItem(CACHE_KEYS.PRODUCTS, JSON.stringify(products));
    localStorage.setItem(CACHE_KEYS.CATEGORIES, JSON.stringify(categories));
    localStorage.setItem(CACHE_KEYS.TIMESTAMP, Date.now().toString());
  }, []);

  // Category helpers
  const organizeCategoriesHierarchy = (catData) => {
    if (!Array.isArray(catData)) return [];
    const main = catData.filter(c => !c.ParentCategory);
    const subs = catData.filter(c => c.ParentCategory);
    return main.map(mainCat => ({
      ...mainCat,
      subcategories: subs.filter(sub => sub.ParentCategory === mainCat.id)
    }));
  };

  const getSubcategoriesForMain = useCallback((mainId) => {
    if (mainId === 'all') return [];
    const main = organizedCategories.find(cat => cat.id === mainId);
    return main?.subcategories || [];
  }, [organizedCategories]);

  // Get display title
  const getDisplayTitle = useCallback(() => {
    if (selectedMainCategory === 'all') return 'All Products';
    
    const mainCat = organizedCategories.find(c => c.id === selectedMainCategory);
    if (!mainCat) return 'Category';
    
    if (selectedSubcategory) {
      const subCat = mainCat.subcategories?.find(s => s.id === selectedSubcategory);
      return subCat ? subCat.CategoryName : mainCat.CategoryName;
    }
    
    return mainCat.CategoryName;
  }, [selectedMainCategory, selectedSubcategory, organizedCategories]);

  // Load data - using optimized fetchShopProducts
  const loadData = useCallback(async () => {
    if (isCacheValid() && loadFromCache()) {
      setLoading(false);
    }

    try {
      const [products, catData] = await Promise.all([
        fetchShopProducts(), // Using optimized function
        fetchCategories()
      ]);

      setAllProducts(products);
      setCategories(catData);
      const organized = organizeCategoriesHierarchy(Object.values(catData));
      setOrganizedCategories(organized);
      saveToCache(products, catData);
    } catch (err) {
      setError("Failed to load products.");
      if (!loadFromCache()) {
        setAllProducts([]);
        setOrganizedCategories([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isCacheValid, loadFromCache, saveToCache]);

  useEffect(() => { loadData(); }, [loadData]);

  // Sync URL params
  useEffect(() => {
    if (categoryId) {
      setSelectedMainCategory(categoryId);
    } else {
      setSelectedMainCategory('all');
    }
    
    if (subcategoryId) {
      setSelectedSubcategory(subcategoryId);
    } else {
      setSelectedSubcategory('');
    }
    
    setSearchTerm('');
    setCurrentPage(1);
  }, [categoryId, subcategoryId]);

  // Search handler
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleMainChange = (e) => {
    const newCat = e.target.value;
    setSelectedMainCategory(newCat);
    setSelectedSubcategory('');
    setCurrentPage(1);
    
    // Navigate to main category only, clear subcategory
    if (newCat !== 'all') {
      navigate(`/shop/category/${newCat}`);
    } else {
      navigate('/shop');
    }
  };

  const handleSubChange = (e) => {
    const newSub = e.target.value;
    setSelectedSubcategory(newSub);
    setCurrentPage(1);
    
    // Update URL to include subcategory
    if (newSub && selectedMainCategory !== 'all') {
      navigate(`/shop/category/${selectedMainCategory}/subcategory/${newSub}`);
    } else if (selectedMainCategory !== 'all') {
      // If subcategory is cleared, go back to main category
      navigate(`/shop/category/${selectedMainCategory}`);
    }
  };

  // NEW: Sort handler
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  // Filtering and Sorting
  const filteredProducts = useMemo(() => {
    let products = [...allProducts];
    
    // Filter by main category
    if (selectedMainCategory !== 'all') {
      const mainCat = organizedCategories.find(cat => cat.id === selectedMainCategory);
      if (mainCat) {
        const ids = [mainCat.id, ...(mainCat.subcategories?.map(s => s.id) || [])];
        products = products.filter(p => (p.PCategories || []).some(id => ids.includes(id)));
      }
    }

    // Filter by subcategory
    if (selectedSubcategory) {
      products = products.filter(p => (p.PCategories || []).includes(selectedSubcategory));
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      products = products.filter(p =>
        (p.name || p.PName || '').toLowerCase().includes(q) ||
        (p.description || p.PDesc || '').toLowerCase().includes(q)
      );
    }
    
    return products;
  }, [allProducts, selectedMainCategory, selectedSubcategory, searchTerm, organizedCategories]);

  // NEW: Sort products
  const sortedProducts = useMemo(() => {
    const products = [...filteredProducts];
    
    switch (sortBy) {
      case 'order':
        // Sort by order number (products without order go last)
        return products.sort((a, b) => {
          const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
          const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        
      case 'name':
        // Sort alphabetically by name
        return products.sort((a, b) => 
          (a.PName || '').localeCompare(b.PName || '')
        );
        
      case 'price-low':
        // Sort by lowest variant price
        return products.sort((a, b) => {
          const priceA = Math.min(...(a.PType || []).map(v => v.PPrice || 0));
          const priceB = Math.min(...(b.PType || []).map(v => v.PPrice || 0));
          return priceA - priceB;
        });
        
      case 'price-high':
        // Sort by highest variant price
        return products.sort((a, b) => {
          const priceA = Math.max(...(a.PType || []).map(v => v.PPrice || 0));
          const priceB = Math.max(...(b.PType || []).map(v => v.PPrice || 0));
          return priceB - priceA;
        });
        
      case 'newest':
        // Sort by date added (newest first)
        return products.sort((a, b) => {
          const dateA = a.PDateAdded ? new Date(a.PDateAdded) : new Date(0);
          const dateB = b.PDateAdded ? new Date(b.PDateAdded) : new Date(0);
          return dateB - dateA;
        });
        
      case 'rating':
        // Sort by rating (highest first)
        return products.sort((a, b) => (b.PRating || 0) - (a.PRating || 0));
        
      default:
        return products;
    }
  }, [filteredProducts, sortBy]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return sortedProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [sortedProducts, currentPage]);

  const totalPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);

  const goToPage = (p) => {
    setCurrentPage(p);
    setVisibleProducts(new Set());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goNext = () => currentPage < totalPages && goToPage(currentPage + 1);
  const goPrev = () => currentPage > 1 && goToPage(currentPage - 1);

  // NEW: Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than or equal to max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate start and end of middle pages
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if we're at the beginning
      if (currentPage <= 2) {
        end = 3;
      }
      // Adjust if we're at the end
      if (currentPage >= totalPages - 1) {
        start = totalPages - 2;
      }
      
      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedMainCategory('all');
    setSelectedSubcategory('');
    setSortBy('order');
    navigate('/shop');
  };

  // Lazy load observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('data-product-id');
          setVisibleProducts(prev => new Set(prev).add(id));
        }
      });
    }, { rootMargin: '100px' });

    observerRef.current = observer;
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!observerRef.current || loading) return;
    const cards = document.querySelectorAll('[data-product-card]');
    cards.forEach(card => observerRef.current.observe(card));
    return () => cards.forEach(card => observerRef.current.unobserve(card));
  }, [paginatedProducts, loading]);

  const displayTitle = getDisplayTitle();
  const currentSubcategories = getSubcategoriesForMain(selectedMainCategory);

  // NEW: Get sort label
  const getSortLabel = () => {
    switch (sortBy) {
      case 'order': return 'Default Order';
      case 'name': return 'Name A-Z';
      case 'price-low': return 'Price: Low to High';
      case 'price-high': return 'Price: High to Low';
      case 'newest': return 'Newest First';
      case 'rating': return 'Highest Rated';
      default: return 'Default Order';
    }
  };

  // Loading Skeleton
  if (loading && allProducts.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="h-8 w-48 mx-auto mb-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-80 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  // Error
  if (error && allProducts.length === 0) {
    return (
      <div className="p-6 text-center max-w-xl mx-auto">
        <p className="text-red-700 mb-4">{error}</p>
        <button onClick={loadData} className="px-6 py-2 bg-red-600 text-white rounded">Retry</button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className='p-5'>'</div>

      {/* Title */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-charcoal">{displayTitle}</h1>
        <p className="text-charcoal/70 mt-2">
          Showing {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
          {sortBy !== 'order' && ` • Sorted by ${getSortLabel()}`}
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col md:flex-row gap-4 mb-10">
        {/* Search */}
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full p-3 pl-10 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary bg-offwhite"
          />
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        </div>

        {/* Category Dropdown */}
        <div className="relative w-full md:w-56">
          <select
            value={selectedMainCategory}
            onChange={handleMainChange}
            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary bg-offwhite appearance-none"
          >
            <option value="all">All Categories</option>
            {organizedCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.CategoryName}</option>
            ))}
          </select>
          <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>

        {/* Subcategory Dropdown */}
        <div className="relative w-full md:w-56">
          <select
            value={selectedSubcategory}
            onChange={handleSubChange}
            disabled={selectedMainCategory === 'all' || currentSubcategories.length === 0}
            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary bg-offwhite appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">All Subcategories</option>
            {currentSubcategories.map(sub => (
              <option key={sub.id} value={sub.id}>{sub.CategoryName}</option>
            ))}
          </select>
          <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>

        {/* NEW: Sort Dropdown */}
        <div className="relative w-full md:w-56">
          <select
            value={sortBy}
            onChange={handleSortChange}
            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary bg-offwhite appearance-none"
          >
            <option value="order">Default Order</option>
            <option value="name">Name A-Z</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="newest">Newest First</option>
            <option value="rating">Highest Rated</option>
          </select>
          <ArrowUpDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>

        {/* Clear */}
        <button
          onClick={handleClearFilters}
          className="px-4 py-3 rounded-lg bg-gray-200 hover:bg-gray-300 text-charcoal transition"
        >
          Clear Filters
        </button>
      </div>


      {/* Product Grid */}
      {paginatedProducts.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
            {paginatedProducts.map((product, index) => (
              <div key={product.id} data-product-card data-product-id={product.id}>
                <ProductCard 
                  product={product} 
                  eagerLoad={visibleProducts.has(product.id)}
                  // NEW: Show order number badge when sorted by order
                  showOrderBadge={sortBy === 'order'}
                  orderNumber={product.order}
                  displayIndex={(currentPage - 1) * PRODUCTS_PER_PAGE + index + 1}
                />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-10">
              <button
                onClick={goPrev}
                disabled={currentPage === 1}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={16} className="mr-1" /> Prev
              </button>

              {/* Page Numbers */}
              <div className="flex gap-1">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`px-3 py-2 border rounded-lg min-w-[40px] transition ${
                        currentPage === page
                          ? 'bg-primary text-white border-primary'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              <button
                onClick={goNext}
                disabled={currentPage === totalPages}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Next <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-beige rounded-lg">
          <p className="text-lg font-semibold text-charcoal mb-4">
            No products found 😔
          </p>
          <button
            onClick={handleClearFilters}
            className="px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark transition"
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(Shop);